import Box from '@mui/material/Box';
import type { SxProps, Theme } from '@mui/material/styles';
import type { KeyboardEvent, MouseEvent } from 'react';

import ProgressiveImage from 'src/common/components/ProgressiveImage';
import { usePageImageLightbox } from 'src/common/components/PageImageLightboxContext';

const lightboxInteractiveSx = {
  cursor: 'zoom-in',
  '&:focus-visible': {
    outline: '2px solid',
    outlineColor: 'primary.main',
    outlineOffset: 2,
  },
} as const;

interface Props {
  src: string;
  alt?: string;
  className?: string;
  title?: string;
  sx?: SxProps<Theme>;
  stopPropagation?: boolean;
}

export default function LightboxImageTrigger({
  src,
  alt,
  className,
  title,
  sx,
  stopPropagation = false,
}: Props) {
  const lightbox = usePageImageLightbox();
  const isLightboxEnabled = lightbox != null && lightbox.images.length > 0;

  const handleOpen = () => {
    lightbox?.openAtSrc(src);
  };

  const handleClick = (event: MouseEvent) => {
    if (stopPropagation) {
      event.stopPropagation();
    }
    handleOpen();
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (!isLightboxEnabled) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (stopPropagation) {
        event.stopPropagation();
      }
      handleOpen();
    }
  };

  return (
    <Box
      className={className}
      title={title}
      onClick={isLightboxEnabled ? handleClick : undefined}
      onKeyDown={isLightboxEnabled ? handleKeyDown : undefined}
      role={isLightboxEnabled ? 'button' : undefined}
      tabIndex={isLightboxEnabled ? 0 : undefined}
      sx={[
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
        isLightboxEnabled ? lightboxInteractiveSx : {},
      ]}
    >
      <ProgressiveImage src={src} alt={alt} objectFit="contain" />
    </Box>
  );
}
